import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import socket from "../../socket";
import "../../styles/projects/ProjectDetails.css";
import { ProjectContext } from "../../App";
import { useAuth } from "../Authentication/AuthContext";
import DatePicker from "react-datepicker";
import { format, parseISO } from "date-fns";
import { useRolePermissions } from "../Settings/RoleContext";
import api from "../../api/axiosConfig";
import EditIcon from "@mui/icons-material/Edit";
import { FaChevronDown } from "react-icons/fa";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { Helmet } from "react-helmet";
import { ToastContainer, toast } from "react-toastify";
import PhaseStatusSelector from "./PhaseStatusSelector";
import Invoices from "./Invoices";

const fetchData = async (url, setData, setError) => {
  try {
    const response = await api.get(url); // Axios automatically adds the baseURL and headers
    setData(response.data); // Axios automatically parses JSON
  } catch (error) {
    console.error("Error fetching data:", error);
    setError(error); // Handle error
  }
};

const addPhase = async (projectId, phase, setError) => {
  try {
    const response = await api.post(`/api/project/${projectId}/phases`, phase);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with a specific error
      console.error("Server error:", error.response.data);
      setError(error.response.data.message || "Failed to add phase.");
    } else if (error.request) {
      // Request was made, but no response received
      console.error("No response received:", error.request);
      setError("Server did not respond. Please try again later.");
    } else {
      // Other errors (e.g., network issues)
      console.error("Unexpected error:", error.message);
      setError(error.message || "An unexpected error occurred.");
    }
  }
};

const deletePhase = async (phaseIds, setError) => {
  try {
    const url = `/api/phases`; // No phaseId in the URL, we're using the request body
    await api.delete(url, { data: { phaseIds } }); // Send phaseIds in the request body
  } catch (error) {
    if (error.response) {
      // If the server responded with an error
      const serverError = error.response.data;
      console.error("Server error:", serverError);
      setError(
        serverError.message ||
          "An error occurred while deleting phases. Please try again."
      );
      alert(
        serverError.message ||
          "An error occurred while deleting phases. Please try again."
      );
      window.location.reload();
    } else {
      // Generic error handling
      console.error("Unexpected error:", error);
      setError("Unexpected error occurred. Please try again.");
    }
  }
};

const ProjectDetails = () => {
  const { hasPermission } = useRolePermissions();
  const { projectId } = useParams();
  const { userDetails } = useAuth();
  const [showInvoiceTable, setShowInvoiceTable] = useState(false);
  const [project, setProject] = useState({
    id: null,
    name: "",
    projectno: "",
    customer_name: "",
    phases: [],
  });
  const [loading, setLoading] = useState(true);
  const profileRef = useRef(null);
  const { dialBoxopen, isNavBarOpen } = useContext(ProjectContext);
  const [error, setError] = useState(null);
  const [statusOptions, setStatusOption] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [phaseEditDialogOpen, setPhaseEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState([]);
  const [phaseId, setPhaseId] = useState([]);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [totalPlannedHours, setTotalPlannedHours] = useState("");
  const [editingPhase, setEditingPhase] = useState(null); // Holds the phase being edited
  const [bulkAction, setBulkAction] = useState(false);
  const [formValues, setFormValues] = useState({
    phaseno: "",
    title: "",
    plannedhours: "",
    start_date: "",
    end_date: "",
  });
  const [newPhase, setNewPhase] = useState({
    title: "",
    plannedhours: 0,
    phaseno: "",
    start_date: "",
    end_date: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUserssPerPage] = useState(13);
  const stageRef = useRef(null);
  const [contentFrom] = useState("phasedetails");
  const [showStage, setShowStage] = useState({});
  const handleProjectScopeChange = async (phaseID, updatedStatuses) => {
    try {
      await api.put(`/api/phasestage/${phaseID}`, { updatedStatuses });
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message); // Debugging Line
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the context menu
      if (stageRef.current && !stageRef.current.contains(event.target)) {
        setShowStage(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    socket.on("connect", () => {
      console.log("Connected to WebSocket server");
    });

    socket.on("new phase added", (newPhase) => {
      if (Number(projectId) === newPhase.project_id) {
        setProject((prev) => {
          // Log the previous phases to ensure it's an array

          const updatedPhases = [
            ...prev.phases,
            {
              phaseno: newPhase.phaseno,
              title: newPhase.title,
              plannedhours: newPhase.plannedhours,
              phase_id: newPhase.id,
              status: newPhase.stage,
              invoice_status: 0,
              invoice_count: 0,
              start_date: newPhase.start_date,
              end_date: newPhase.end_date,
            },
          ];

          return {
            ...prev,
            phases: updatedPhases,
          };
        });
      }
    });

    socket.on("phase updated", (updatedPhase) => {
      setProject((prevProject) => {
        const updatedPhases = prevProject.phases.map((phase) =>
          phase.phase_id === Number(updatedPhase.id)
            ? {
                ...phase,
                title: updatedPhase.title,
                start_date: updatedPhase.formattedStartDate,
                end_date: updatedPhase.formattedEndDate,
              }
            : phase
        );

        return {
          ...prevProject,
          phases: [...updatedPhases],
        };
      });
    });

    socket.on("phase deleted", (data) => {
      // Ensure that `phaseIds` is an array
      const idsArray = Array.isArray(data.phaseIds)
        ? data.phaseIds
        : [data.phaseIds];

      setProject((prev) => {
        // Filter out the phases whose IDs are in the selectedPhaseId array
        const updatedPhases = prev.phases.filter(
          (phase) => !idsArray.includes(phase.phase_id) // Remove phases whose IDs are in selectedPhaseId
        );

        return {
          ...prev,
          phases: updatedPhases, // Update phases after deletion
        };
      });
    });

    socket.on("phase_stage_updated", ({ phaseID, stage }) => {
      setProject((prevProject) => ({
        ...prevProject,
        phases: prevProject.phases.map((phase) =>
          phase.phase_id === Number(phaseID)
            ? { ...phase, phase_scopes: stage }
            : phase
        ),
      }));
    });

    socket.on("invoice added", (newInvoice) => {
      fetchData(
        `/api/phase/invoices/${newInvoice.project_id}/${newInvoice.phase_id}`,
        setInvoices,
        setError
      );
    });

    return () => {
      socket.off("new phase added");
      socket.off("phase deleted");
      socket.off("phase updated");
      socket.off("phase_stage_updated");
      socket.off("invoice added");
    };
  }, []);

  const updateProjectsPerPage = () => {
    if (window.innerWidth < 1919) {
      setUserssPerPage(14); // Small screens (e.g., mobile)
    } else if (window.innerWidth >= 1920) {
      setUserssPerPage(20); // Medium screens (e.g., tablet)
    } else {
      setUserssPerPage(10); // Large screens (e.g., desktop)
    }
  };
  useEffect(() => {
    // Set initial projects per page
    updateProjectsPerPage();

    // Update projects per page when the window is resized
    window.addEventListener("resize", updateProjectsPerPage);

    // Cleanup event listener on unmount
    return () => window.removeEventListener("resize", updateProjectsPerPage);
  }, []);

  // useEffect that calculates claimPercent
  useEffect(() => {
    // Fetch data and calculate claim percentage
    fetchData(
      `/api/project/${projectId}/phasesplannedhours`,
      (data) => {
        const sanitizedPhases = Array.isArray(data.phases)
          ? data.phases
              .filter(
                (phase) =>
                  phase.phase_id !== null &&
                  phase.title !== null &&
                  phase.task_count !== null
              )
              .map((phase) => {
                return phase;
              })
          : [];

        // Calculate total planned hours in decimal format
        const totalDecimalHours = sanitizedPhases.reduce((total, phase) => {
          // Convert "planned_hours" to hours in decimal format (HH:mm -> hours)
          const [hours, minutes] = phase.planned_hours.split(":").map(Number);
          const decimalHours = hours + minutes / 60;
          return total + decimalHours;
        }, 0);

        // Convert totalDecimalHours back to hh:mm format
        const totalHours = Math.floor(totalDecimalHours); // Get hours
        const totalMinutes = Math.round((totalDecimalHours - totalHours) * 60); // Get minutes

        // Format total planned hours in hh:mm format
        const formattedTotalPlannedHours = `${totalHours
          .toString()
          .padStart(2, "0")}:${totalMinutes.toString().padStart(2, "0")}`;

        // Store formatted total planned hours in the state
        setTotalPlannedHours(formattedTotalPlannedHours);

        // Set the project data with sanitized phases
        setProject({ ...data, phases: sanitizedPhases });
      },
      setError
    ).finally(() => setLoading(false));
    fetchData("/api/status", setStatusOption, setError);
  }, [projectId, showInvoiceTable]);

  const handleOpenPhaseDialog = () => {
    let defaultPhasetitle;
    if (project.phases.length === 0) {
      defaultPhasetitle = project.project_name;
    } else {
      defaultPhasetitle = `Phase${project.phases.length + 1}`;
    }
    const defaultPhaseNo = `P${project.phases.length + 1}`;

    setNewPhase({
      project_id: project.id,
      title: defaultPhasetitle,
      plannedhours: 0,
      phaseno: defaultPhaseNo,
    });
    setPhaseDialogOpen(true);
  };

  const handleClosePhaseDialog = () => {
    setPhaseDialogOpen(false);
  };

  const handleAddPhase = async () => {
    const newPhaseData = await addPhase(projectId, newPhase, setError);

    setPhaseDialogOpen(false);
  };

  const handleEditClick = (phase) => {
    setEditingPhase(phase.phase_id);

    setFormValues({
      phaseno: phase.phaseno,
      title: phase.title,
      plannedhours: phase.plannedhours,
      start_date: phase.start_date,
      end_date: phase.end_date,
    });
    setPhaseEditDialogOpen(true);
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSaveClick = async () => {
    const { phaseno, title, plannedhours, start_date, end_date } = formValues;
    const phaseId = editingPhase;
    try {
      const response = await api.put(`/api/phase/${phaseId}`, {
        phaseno,
        title,
        plannedhours,
        start_date,
        end_date,
      });
      // Axios responses don't have .ok, they throw errors automatically
      const updatedPhase = response.data;
      const updatedPhases = project.phases.map((phase) =>
        phase.phase_id === phaseId
          ? {
              ...phase,
              phaseno: updatedPhase.phaseno,
              title: updatedPhase.title,
              plannedhours: updatedPhase.plannedhours,
              id: updatedPhase.phase_id,
              start_date: updatedPhase.formattedStartDate,
              end_date: updatedPhase.formattedEndDate,
            }
          : phase
      );
      setProject((prevProject) => ({
        ...prevProject,
        phases: updatedPhases,
      }));
      setPhaseEditDialogOpen(false);
    } catch (error) {
      // Axios errors are inside error.response
      let errorMessage = "Failed to update phase";
      if (error.response) {
        errorMessage = error.response.data.message || errorMessage;
      }
      console.error(errorMessage);
      console.log(errorMessage);
    }
  };

  const fetchInvoiceData = (phaseId) => {
    fetchData(
      `/api/phase/invoices/${projectId}/${phaseId}`,
      setInvoices,
      setError
    );
  };

  const handleDeletePhaseClick = (e, phaseId) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      // Add phaseId to the selectedPhaseId array
      setSelectedPhaseId((prev) => [...prev, phaseId]);
    } else {
      // Remove phaseId from the selectedPhaseId array
      setSelectedPhaseId((prev) => prev.filter((id) => id !== phaseId));
    }
  };
  //delete phase
  const handleDeleteConfirm = async () => {
    if (isCheckboxChecked) {
      try {
        // Call the deletePhase function with the selected phase IDs
        await deletePhase(selectedPhaseId, setError);
        // Update the state after deletion
        setProject((prev) => {
          // Filter out the phases whose IDs are in the selectedPhaseId array
          const updatedPhases = prev.phases.filter(
            (phase) => !selectedPhaseId.includes(phase.phase_id) // Remove phases whose IDs are in selectedPhaseId
          );

          return {
            ...prev,
            phases: updatedPhases, // Update phases after deletion
          };
        });

        // Close the delete dialog and reset UI states
        setDeleteDialogOpen(false);
        setBulkAction(false);
        setIsCheckboxChecked(false); // Reset the checkbox state
        setSelectedPhaseId([]); // Clear the selected phases after deletion
      } catch (error) {
        console.error("Failed to delete phase:", error); // Log the error for debugging
        setError(error); // Display the error message to the user
      }
    } else {
      toast.error("Please check the checkbox to confirm deletion.", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  //close dial and remove no-scroll
  const handleCloseDial = () => {
    setIsCheckboxChecked(false);
    setDeleteDialogOpen(false);
    setSelectedPhaseId([]);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setBulkAction(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
  }, []);

  const totalPages = Math.ceil(project.phases.length / usersPerPage);

  // Get current projects to display
  const indexOfLastUsers = currentPage * usersPerPage;
  const indexOfFirstUsers = indexOfLastUsers - usersPerPage;
  const currentProjects = project.phases.slice(
    indexOfFirstUsers,
    indexOfLastUsers
  );

  // Handlers for pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleFirstPage = () => {
    if (currentPage > 1) setCurrentPage(1);
  };
  const handleLastPage = () => {
    if (currentPage < totalPages) setCurrentPage(totalPages);
  };

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );
  if (error)
    return (
      <p color="error" className="error-message">
        Error: {error.message}
      </p>
    );

  const convertToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };

  return (
    <>
      <Helmet>
        <title>Msuite - Projects</title>
      </Helmet>
      <ToastContainer />
      {selectedPhaseId.length > 0 && !dialBoxopen && (
        <button
          onClick={() => setBulkAction((prev) => !prev)}
          className="add-btn mass-update-phase"
        >
          Mass Update
        </button>
      )}
      {hasPermission("phases", "create") && !dialBoxopen && (
        <button
          onClick={handleOpenPhaseDialog}
          className={
            selectedPhaseId.length > 0 ? "add-phase-btn" : "add-phase-btn-close"
          }
          title="Add Phase"
        >
          New Phase
        </button>
      )}
      {bulkAction && (
        <div className="action-box" ref={profileRef}>
          {hasPermission("phases", "delete") && (
            <button
              onClick={() => setDeleteDialogOpen(true)}
              title="Delete Phase"
              className="add-btn"
              style={{ backgroundColor: "Red" }}
            >
              Delete
            </button>
          )}
        </div>
      )}

      {!dialBoxopen && (
        <div
          className={
            isNavBarOpen
              ? "project-phase-details"
              : "project-phase-details-close"
          }
        >
          <div style={{ marginLeft: "103px" }}>
            <div>
              {project.phases.length === 0 ? (
                <div className="phase-image-container">
                  <img className="phase-image" src="/AppLayout/Phase.png" />
                  <p className="phase-message">
                    No phases available for this project
                  </p>
                </div>
              ) : (
                <>
                  <div
                    className={
                      isNavBarOpen ? "table-container" : "table-container-close"
                    }
                  >
                    <table
                      className={
                        isNavBarOpen ? "phase-table" : "phase-table-close"
                      }
                    >
                      <thead>
                        <tr>
                          {hasPermission("phases", "delete") && (
                            <th style={{ width: "2px", textAlign: "center" }}>
                              <FaChevronDown />
                            </th>
                          )}
                          <th
                            style={{
                              color: "#616161",
                              color: "black",
                              width: "85px",
                            }}
                          >
                            Phase No
                          </th>
                          <th
                            style={{
                              color: "#616161",
                              color: "black",
                              width: "105px",
                            }}
                          >
                            Phase Title
                          </th>
                          <th
                            style={{
                              color: "#616161",
                              color: "black",
                              width: "120px",
                            }}
                          >
                            Scope
                          </th>
                          <th
                            style={{
                              color: "#616161",
                              color: "black",
                              width: "90px",
                            }}
                          >
                            Planned Hrs
                          </th>
                          <th
                            style={{
                              color: "#616161",
                              color: "black",
                              width: "70px",
                            }}
                          >
                            Actual Hrs
                          </th>
                          {hasPermission("invoices", "read") && (
                            <th
                              style={{
                                color: "#616161",
                                color: "black",
                                width: "25px",
                                textAlign: "center",
                              }}
                            >
                              %
                            </th>
                          )}
                          {hasPermission("invoices", "create") && (
                            <th
                              style={{
                                color: "#616161",
                                color: "black",
                                width: "55px",
                              }}
                            >
                              Invoices
                            </th>
                          )}
                          <th style={{ width: "20px" }}>No of VO(UC)</th>
                          <th style={{ width: "50px" }}>VO PHrs(UC)</th>
                          <th style={{ width: "20px" }}>No of VO</th>
                          <th style={{ width: "40px" }}>VO PHrs</th>
                          <th style={{ width: "40px" }}>VO AHrs</th>
                          <th style={{ width: "40px" }}>Start Date</th>
                          <th style={{ width: "40px" }}>End Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {currentProjects.map((phase, index) => (
                          <tr key={phase.id}>
                            {hasPermission("phases", "delete") && (
                              <td>
                                <input
                                  style={{ cursor: "pointer" }}
                                  type="checkbox"
                                  name={phase.name}
                                  checked={selectedPhaseId.includes(
                                    phase.phase_id
                                  )}
                                  onChange={(e) =>
                                    handleDeletePhaseClick(e, phase.phase_id)
                                  }
                                />
                              </td>
                            )}
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  whiteSpace: "nowrap",
                                  alignItems: "center",
                                  height: "27px",
                                  minwidth: "140px",
                                  maxwidth: "100%",
                                }}
                              >
                                <Link
                                  to={`/project/${projectId}/phase/${phase.phase_id}`}
                                  className="phase-tile"
                                >
                                  {phase.phaseno}
                                </Link>
                                {hasPermission("phases", "update") && (
                                  <button
                                    onClick={() => {
                                      handleEditClick(phase);
                                    }}
                                    className="edit-icon-wrapper"
                                    style={{
                                      backgroundColor: "transparent",
                                      border: "none",
                                    }}
                                    title="Edit Phase"
                                  >
                                    <EditIcon
                                      style={{
                                        color: "blue",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td
                              style={{
                                minwidth: "auto",
                                maxwidth: "100%",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {phase.title}
                            </td>
                            <td
                              style={{
                                minWidth: "auto",
                                maxWidth: "100%",
                                whiteSpace: "nowrap",
                                padding: "0px",
                                textAlign: "center",
                                position: "relative",
                                cursor: "pointer",
                              }}
                              ref={stageRef}
                              onClick={() =>
                                setShowStage((prev) => ({
                                  ...prev,
                                  [phase.phase_id]: true,
                                }))
                              }
                            >
                              <div
                                className="status-wrapper"
                                style={{
                                  marginLeft: "10px",
                                  marginRight: "10px",
                                }}
                              >
                                {!showStage[phase.phase_id] ? (
                                  <div
                                    className="status-display"
                                    style={{
                                      backgroundColor: "transparent",
                                      textAlign: "left",
                                    }}
                                    onClick={() =>
                                      setShowStage((prev) => ({
                                        ...prev,
                                        [phase.phase_id]: true,
                                      }))
                                    }
                                  >
                                    {(() => {
                                      let parsedScopes;
                                      if (Array.isArray(phase.phase_scopes)) {
                                        parsedScopes = phase.phase_scopes;
                                      } else if (
                                        typeof phase.phase_scopes === "string"
                                      ) {
                                        parsedScopes = phase.phase_scopes
                                          .split(",")
                                          .map((s) => s.trim());
                                      } else {
                                        parsedScopes = [];
                                      }

                                      const visibleScopes = parsedScopes.slice(
                                        0,
                                        3
                                      );
                                      const remainingCount =
                                        parsedScopes.length - 3;

                                      return parsedScopes.length > 0 ? (
                                        <>
                                          {visibleScopes.map((status) => {
                                            const statusObj =
                                              statusOptions?.find(
                                                (s) => s.name === status
                                              ) || {};
                                            return (
                                              <span
                                                key={status}
                                                className="status-badge"
                                                style={{
                                                  backgroundColor:
                                                    statusObj.color || "white",
                                                  marginRight: "5px",
                                                  borderRadius: "3px",
                                                  padding: "3px",
                                                  fontSize: "12px",
                                                }}
                                              >
                                                {status}
                                              </span>
                                            );
                                          })}
                                          {remainingCount > 0 && (
                                            <span className="more-status">
                                              +{remainingCount} more
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="placeholder-text"></span>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="stage-display-container">
                                    <PhaseStatusSelector
                                      stageRef={stageRef}
                                      project={phase}
                                      statusOptions={statusOptions}
                                      handleProjectScopeChange={
                                        handleProjectScopeChange
                                      }
                                      hasPermission={hasPermission}
                                      contentFrom={contentFrom}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>

                            <td style={{ textAlign: "center" }}>
                              <Link
                                to={`/project/${projectId}/phase/${phase.phase_id}`}
                                className="phase-tile"
                                onClick={() =>
                                  localStorage.setItem("tabIndex", 0)
                                }
                              >
                                {phase.planned_hours !== "00:00"
                                  ? phase.planned_hours
                                  : ""}
                              </Link>
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                color:
                                  convertToMinutes(phase.actualhours) <
                                  convertToMinutes(phase.planned_hours)
                                    ? "green"
                                    : "red",
                              }}
                            >
                              {phase.actualhours !== "00:00"
                                ? phase.actualhours
                                : ""}
                            </td>
                            {hasPermission("invoices", "read") && (
                              <td
                                style={{
                                  textAlign: "center",
                                  fontWeight: "700",
                                  color:
                                    Number(phase.invoice_status) === 100
                                      ? "#40af48"
                                      : "orange",
                                }}
                              >
                                {phase.invoice_status === 0
                                  ? ""
                                  : phase.invoice_status}
                              </td>
                            )}
                            {hasPermission("invoices", "create") && (
                              <td>
                                <span
                                  style={{
                                    cursor: "pointer",
                                    opacity:
                                      Number(phase.invoice_status) === 100
                                        ? 0.5
                                        : 1,
                                  }}
                                  onClick={() => {
                                    if (hasPermission("invoices", "create")) {
                                      fetchInvoiceData(phase.phase_id);
                                      setShowInvoiceTable(true);
                                      setPhaseId(phase.phase_id);
                                    }
                                  }}
                                >
                                  {phase.invoice_count === 0 ? (
                                    <LiaFileInvoiceSolid className="invoice-icon" />
                                  ) : (
                                    `Count :${phase.invoice_count || ""}`
                                  )}
                                </span>
                              </td>
                            )}
                            <td style={{ textAlign: "center" }}>
                              <Link
                                onClick={() =>
                                  localStorage.setItem("tabIndex", 1)
                                }
                                to={`/project/${projectId}/phase/${phase.phase_id}`}
                                className="phase-tile"
                              >
                                {phase.unclaimed_variation_count !== 0 &&
                                  phase.unclaimed_variation_count}
                              </Link>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {phase.unclaimed_variation_planned_hours !==
                                "00:00" &&
                                phase.unclaimed_variation_planned_hours}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {phase.variation_count !== 0 &&
                                phase.variation_count}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {phase.total_variation_planned_hours !==
                                "00:00" && phase.total_variation_planned_hours}
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                color:
                                  convertToMinutes(
                                    phase.total_variation_actual_hours
                                  ) <
                                  convertToMinutes(
                                    phase.total_variation_planned_hours
                                  )
                                    ? "green"
                                    : "red",
                              }}
                            >
                              {phase.total_variation_actual_hours !== "00:00" &&
                                phase.total_variation_actual_hours}
                            </td>
                            <td>
                              {phase.start_date !== null
                                ? format(phase.start_date, "dd-MM-yyyy")
                                : ""}
                            </td>
                            <td>
                              {phase.end_date !== null
                                ? format(phase.end_date, "dd-MM-yyyy")
                                : ""}
                            </td>
                            <td></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className={
                      isNavBarOpen
                        ? "phase-hours-summary"
                        : "phase-hours-summary-close"
                    }
                  >
                    <h3
                      className={
                        isNavBarOpen
                          ? "phase-details-text"
                          : "phase-details-text-close"
                      }
                    >
                      Phase Planned Hours Summary : {totalPlannedHours}
                    </h3>
                    <div
                      className={
                        isNavBarOpen
                          ? "phase-pagination"
                          : "phase-pagination-close"
                      }
                    >
                      <p className="pagination-total">
                        Total : {project.phases.length}
                      </p>
                      <button
                        onClick={handleFirstPage}
                        disabled={currentPage === 1}
                        className="phase-arrow-btn"
                      >
                        <MdKeyboardDoubleArrowLeft />
                      </button>
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="phase-arrow-btn"
                      >
                        {/* */}
                        <MdKeyboardArrowLeft />
                      </button>
                      <span
                        style={{
                          marginTop: "5px",
                          textWrap: "nowrap",
                          backgroundColor: "transparent",
                        }}
                      >
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="phase-arrow-btn"
                      >
                        <MdKeyboardArrowRight />
                      </button>
                      <button
                        onClick={handleLastPage}
                        disabled={currentPage === totalPages}
                        className="phase-arrow-btn"
                      >
                        <MdKeyboardDoubleArrowRight />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Add Phase Dialog */}
              {phaseDialogOpen && (
                <div className="add-phase-dial">
                  <div className="add-phase-con">
                    <div className="dial-v-header">
                      <h3 className="add-edit-variation">New Phase</h3>
                    </div>

                    <div className="add-phase-content">
                      <h3 className="add-phase-heading">Phase No</h3>
                      <input
                        label="Phase Title"
                        className="phase-input"
                        value={newPhase.phaseno}
                        type="text"
                        onChange={(e) =>
                          setNewPhase({ ...newPhase, phaseno: e.target.value })
                        }
                      />
                      <h3 className="add-phase-heading">Phase Title</h3>
                      <input
                        label="Phase No"
                        className="phase-input"
                        value={newPhase.title}
                        type="text"
                        onChange={(e) =>
                          setNewPhase({ ...newPhase, title: e.target.value })
                        }
                      />
                      <h3 className="add-phase-heading">Start Date</h3>
                      <DatePicker
                        placeholderText="dd-mm-yyyy"
                        selected={
                          newPhase.start_date
                            ? new Date(newPhase.start_date)
                            : null
                        }
                        onChange={(date) =>
                          setNewPhase({
                            ...newPhase,
                            start_date: date || null, // Ensure null is set when clearing the date
                          })
                        }
                        dateFormat="dd-MM-yyyy"
                        className="phase-date-input"
                        minDate={
                          project.project_startdate
                            ? new Date(project.project_startdate)
                            : null
                        }
                        maxDate={
                          newPhase.end_date
                            ? new Date(newPhase.end_date)
                            : new Date(project.project_enddate)
                        }
                      />
                      <h3 className="add-phase-heading">End Date</h3>
                      <DatePicker
                        placeholderText="dd-mm-yyyy"
                        selected={
                          newPhase.end_date ? new Date(newPhase.end_date) : null
                        }
                        onChange={(date) =>
                          setNewPhase({
                            ...newPhase,
                            end_date: date || null, // Ensure null is set when clearing the date
                          })
                        }
                        dateFormat="dd-MM-yyyy"
                        className="phase-date-input"
                        minDate={
                          newPhase.start_date
                            ? new Date(newPhase.start_date)
                            : new Date(project.project_startdate)
                        }
                        maxDate={
                          project.project_enddate
                            ? new Date(project.project_enddate)
                            : null
                        }
                      />
                    </div>
                    <div className="btn-con-second">
                      <button
                        onClick={handleAddPhase}
                        style={{ marginLeft: "26px", width: "50px" }}
                        className="add-phase-dial-btn"
                      >
                        Add
                      </button>
                      <button
                        onClick={handleClosePhaseDialog}
                        className="cancel-btn"
                        style={{ cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className={`delete-phase-dial ${dialBoxopen ? "open" : "close"} `}>
          <div className="delete-dial">
            <div className="dial-d-header">
              <h3
                style={{ backgroundColor: "transparent", marginLeft: "15px" }}
              >
                Delete Phase
              </h3>
            </div>
            <div style={{ backgroundColor: "white" }}>
              <p style={{ backgroundColor: "white" }}>
                Are you sure you want to delete this phase? This action cannot
                be undone.
              </p>
              <input
                style={{ cursor: "pointer" }}
                type="checkbox"
                id="delete-phase"
                checked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked((prev) => !prev)}
              />
              <label
                htmlFor="delete-phase"
                style={{ backgroundColor: "white", cursor: "pointer" }}
              >
                I understand this action cannot be undone.
              </label>
            </div>
            <div className="delete-dial-btn-con">
              <button
                onClick={handleDeleteConfirm}
                color="secondary"
                variant="contained"
                disabled={!isCheckboxChecked}
                className="add-btn"
                style={{ backgroundColor: isCheckboxChecked ? "red" : "gray" }}
              >
                Delete
              </button>
              <button onClick={handleCloseDial} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {phaseEditDialogOpen && editingPhase && (
        <div className="add-phase-dial">
          <div className="add-phase-con">
            <div className="dial-v-header">
              <h3 className="add-edit-variation">Edit Phase</h3>
            </div>
            <div className="edit-phase-content">
              <h3 className="issue-label">Phase No</h3>
              <input
                type="text"
                className="issue-number"
                name="phaseno"
                value={formValues.phaseno}
                onChange={handleInputChange}
              />

              <h3 className="issue-label">Phase Title</h3>
              <input
                type="text"
                className="issue-number"
                name="title"
                value={formValues.title}
                onChange={handleInputChange}
              />
              <h3 className="add-phase-heading">Start Date</h3>
              <DatePicker
                placeholderText="dd-mm-yyyy"
                name="start_date"
                selected={
                  formValues.start_date ? new Date(formValues.start_date) : null
                }
                onChange={(date) =>
                  setFormValues({ ...formValues, start_date: date })
                }
                dateFormat="dd-MM-yyyy"
                className="phase-date-input"
                minDate={
                  project.project_startdate
                    ? new Date(project.project_startdate)
                    : null
                }
                maxDate={
                  project.project_enddate
                    ? new Date(project.project_enddate)
                    : null
                }
              />
              <h3 className="add-phase-heading">End Date</h3>
              <DatePicker
                placeholderText="dd-mm-yyyy"
                name="end_date"
                selected={
                  formValues.end_date ? new Date(formValues.end_date) : null
                }
                onChange={(date) =>
                  setFormValues({ ...formValues, end_date: date })
                }
                dateFormat="dd-MM-yyyy"
                className="phase-date-input"
                minDate={
                  project.project_startdate
                    ? new Date(project.project_startdate)
                    : null
                }
                maxDate={
                  project.project_enddate
                    ? new Date(project.project_enddate)
                    : null
                }
              />
            </div>
            <div className="edit-btn-con-second">
              <button className="add-edit-issue-btn" onClick={handleSaveClick}>
                Save
              </button>
              <button
                className="cancel-btn"
                style={{ marginRight: "140px", marginLeft: "0px" }}
                onClick={() => setPhaseEditDialogOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showInvoiceTable && invoices && (
        <Invoices
          phaseId={phaseId}
          projectId={projectId}
          showInvoiceTable={showInvoiceTable}
          setShowInvoiceTable={setShowInvoiceTable}
        />
      )}
    </>
  );
};

export default ProjectDetails;
